import { useCallback, useState } from 'react';
import Firebase from '../contexts/firebase/firebase';
import type { ClientApplication } from '@shopify/app-bridge';

type TAuthResponse = {
  status: string;
  token: string;
}

export const useShopifyAuth = (
  firebase: Firebase, 
  app: ClientApplication, 
  shop: string, 
  getSessionToken: Function
) => {
  const [initialized, setInitialized] = useState<boolean>(false);
  const [isError, setError] = useState<boolean>(false);

  const handleAuthorize = useCallback(async () => {
    try {
      // Get session token from App Bridge
      const shopifyAuthToken = await getSessionToken(app);
      if (typeof shopifyAuthToken !== "string") throw new Error(`Invalid auth token returned. Expected a string response.`);

      // Prepare login fetch request
      const fetchUrl = `${process.env.REACT_APP_CLOUD_FUNCTIONS_URI}/ShopifyApp/session`;
      const fetchOptions = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shop,
          token: shopifyAuthToken,
        }),
      };

      // Execute login fetch
      const authResponse = await fetch(fetchUrl, fetchOptions);
      if (!authResponse.ok) throw new Error(`Session callback returned with status ${authResponse.status}`);

      // Get response body
      const authResponseBody: TAuthResponse = await authResponse.json();

      // Authenticate with firestore using a custom token
      const isAuthenticated = await firebase.authenticate(authResponseBody.token);

      if (!isAuthenticated) throw new Error(`Unable to authenticate with Firestore`);

      setInitialized(true);

    } catch (error) {
      console.error('Error while authorizing: ', error);
      setError(true);
      setInitialized(true);
    }
  }, [firebase, app, shop, getSessionToken]);

  return {
    initialized,
    isError,
    handleAuthorize,
  };
};
